import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchStorage,
  fetchStorages,
  updateStorage,
  createStorage,
  deleteStorage,
} from "../../api/inventory/storage";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  storage: {},
  listRequestId: null,
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteStorageAsync = createAsyncThunk(
  "inventory_storages/deleteStorageAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteStorage(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "delete_inventory_storage");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_inventory_storage");
    }
  }
);

export const createStorageAsync = createAsyncThunk(
  "inventory_storages/createStorageAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createStorage(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_inventory_storage");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_inventory_storage");
    }
  }
);

export const updateStorageAsync = createAsyncThunk(
  "inventory_storages/updateStorageAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateStorage(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_inventory_storage");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_inventory_storage");
    }
  }
);

export const getStorages = createAsyncThunk(
  "inventory_storages/getStorages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchStorages();
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "list_inventory_storages");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_inventory_storages");
    }
  }
);

export const getStorageAsync = createAsyncThunk(
  "inventory_storages/getStorageAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchStorage(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_inventory_storage");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_inventory_storage");
    }
  }
);

const InventoryStorageSlice = createSlice({
  name: "inventory_storages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStorages.pending, (state, action) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
        state.data = [];
        state.listRequestId = action.meta.requestId;
      })
      .addCase(getStorages.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        state.data = action.payload;
        state.listRequestId = null;
      })
      .addCase(getStorages.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente lagerlokasjonene.");
        state.listRequestId = null;
      })
      .addCase(updateStorageAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateStorageAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (item) => item.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateStorageAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere lagerlokasjonen."
        );
      })
      .addCase(createStorageAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createStorageAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createStorageAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette lagerlokasjonen."
        );
      })
      .addCase(deleteStorageAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteStorageAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteStorageAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette lagerlokasjonen."
        );
      })
      .addCase(getStorageAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getStorageAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.storage = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getStorageAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente lagerlokasjonen."
        );
      });
  },
});

export const { reducer } = InventoryStorageSlice;

export default InventoryStorageSlice;

export const selectInventoryStorageById = (state, inventoryStorageId) =>
  state.inventory_storages.data.find(
    (storage) => storage.id == inventoryStorageId
  );
